import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import {
    CommodityModel,
    CounterpartyModel,
    LocationModel,
    PortfolioModel,
    GetCustomerDataResponse,
    ContractDate,
    Side,
    PositionType,
    Unit,
    Currency
} from '../../../../Generated/Commodity-Management-Api';

/**
 * Parser for multi-tab "trade update" workbooks (e.g. Diageo May 26 Canada/US).
 *
 * Each tab is treated independently. The position type is inferred from the tab name
 * (swap/future). EFP tabs are ignored. Columns are loosely matched to the AddTrade
 * inputs that the existing chat assistant pipeline already understands, so the rows
 * can be reviewed and uploaded via the existing flow.
 *
 * This file does NOT modify the existing single-sheet parser model. It is only used
 * when the workbook is recognised as multi-tab trade-update format.
 */

// Shape matching ParsedPositionRow in ChatAssistant.tsx (kept in sync structurally).
export interface MultiTabParsedRow {
    rowNumber: number;
    id: string;
    sheetName?: string;
    data: {
        commodityName?: string;
        counterpartyName?: string;
        locationName?: string;
        portfolioName?: string;
        tradeDate?: string;
        dealTicketNumber?: string;
        volume: number;
        price: number;
        unit: Unit;
        currency: Currency;
        side: Side;
        positionType: PositionType;
        budgetDate: ContractDate;
        contractDate: ContractDate;
    };
    mappedData: {
        commodity?: CommodityModel;
        counterparty?: CounterpartyModel;
        location?: LocationModel;
        portfolio?: PortfolioModel;
    };
    unmappedData: Record<string, any>;
    errors: { field: string; message: string }[];
    warnings: { field: string; message: string }[];
}

// --- Loose fuzzy matching (kept local so this module is self-contained) ---

function fuzzy(searchTerm: string, target: string): number {
    if (!searchTerm || !target) return 0;
    const s = String(searchTerm).toLowerCase().trim();
    const t = String(target).toLowerCase().trim();
    if (s === t) return 1.0;
    if (t.includes(s) || s.includes(t)) return 0.8;
    const sWords = s.split(/[\s&,_\-/]+/).filter(w => w.length > 1);
    const tWords = t.split(/[\s&,_\-/]+/).filter(w => w.length > 1);
    let matches = 0;
    for (const sw of sWords) {
        for (const tw of tWords) {
            if (tw.includes(sw) || sw.includes(tw)) { matches++; break; }
        }
    }
    if (sWords.length > 0) {
        const score = matches / Math.max(sWords.length, tWords.length);
        if (score > 0.5) return score * 0.7;
    }
    return 0;
}

function bestMatch<T extends Record<string, any>>(
    searchTerm: string | undefined,
    items: T[] | null | undefined,
    nameField: string,
    minScore: number = 0.4
): T | undefined {
    if (!searchTerm || !items?.length) return undefined;
    let best: T | undefined;
    let bestScore = 0;
    for (const item of items) {
        const score = fuzzy(searchTerm, item[nameField] || '');
        if (score > bestScore) { bestScore = score; best = item; }
    }
    return bestScore >= minScore ? best : undefined;
}

function looseEnumMatch<T extends Record<string, string>>(value: any, enumObj: T): T[keyof T] | undefined {
    if (value === undefined || value === null || value === '') return undefined;
    const str = String(value).trim();
    const values = Object.values(enumObj) as string[];
    // Exact (case-insensitive) match on enum value
    const exact = values.find(v => v.toLowerCase() === str.toLowerCase());
    if (exact) return exact as T[keyof T];
    // Match on enum key
    const keyHit = Object.keys(enumObj).find(k => k.toLowerCase() === str.toLowerCase());
    if (keyHit) return enumObj[keyHit] as T[keyof T];
    // Loose contains match
    let best: string | undefined;
    let bestScore = 0;
    for (const v of values) {
        const score = fuzzy(str, v);
        if (score > bestScore) { bestScore = score; best = v; }
    }
    return bestScore >= 0.5 ? (best as T[keyof T]) : undefined;
}

// --- Header / column resolution ---

function findHeaderKey(row: Record<string, any>, aliases: string[]): string | undefined {
    const keys = Object.keys(row);
    const lowerMap = new Map(keys.map(k => [k.toLowerCase().trim(), k]));
    for (const alias of aliases) {
        const hit = lowerMap.get(alias.toLowerCase().trim());
        if (hit) return hit;
    }
    // Loose contains match on header text
    for (const alias of aliases) {
        const a = alias.toLowerCase();
        for (const k of keys) {
            const kl = k.toLowerCase();
            if (kl.includes(a) || a.includes(kl)) return k;
        }
    }
    return undefined;
}

function getColValue(row: Record<string, any>, aliases: string[]): any {
    const key = findHeaderKey(row, aliases);
    return key !== undefined ? row[key] : undefined;
}

// --- Date helpers ---

function excelSerialToDayjs(value: any): dayjs.Dayjs | null {
    if (value === undefined || value === null || value === '') return null;
    if (typeof value === 'number') {
        // Excel serial -> UTC ms at midnight UTC. Extract Y/M/D from the UTC parts
        // and rebuild a dayjs at local midnight so that subsequent .format('YYYY-MM-DD')
        // doesn't shift to the previous day in negative-UTC timezones.
        const utc = new Date(Math.round((value - 25569) * 86400 * 1000));
        return dayjs(new Date(utc.getUTCFullYear(), utc.getUTCMonth(), utc.getUTCDate()));
    }
    if (value instanceof Date) {
        // XLSX with cellDates:true returns Date objects at UTC midnight; normalize the
        // same way to avoid the local-timezone shift.
        return dayjs(new Date(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate()));
    }
    const d = dayjs(value);
    return d.isValid() ? d : null;
}

function dateToMonthYear(value: any): { month: number; year: number } | null {
    const d = excelSerialToDayjs(value);
    if (!d) return null;
    return { month: d.month() + 1, year: d.year() };
}

function dateToIso(value: any): string | undefined {
    const d = excelSerialToDayjs(value);
    return d ? d.format('YYYY-MM-DD') : undefined;
}

// --- Position type from tab name ---

function positionTypeFromTabName(tabName: string): PositionType | null {
    const n = tabName.toLowerCase();
    if (n.includes('efp')) return null; // explicitly skipped
    if (n.includes('swap')) return PositionType.Swap;
    if (n.includes('future')) return PositionType.Future;
    if (n.includes('option')) return PositionType.Option;
    if (n.includes('physical')) return PositionType.Physical;
    return null;
}

// --- Format detection ---

const TRADE_HEADER_REQUIRED = ['type', 'effective date'];
const TRADE_HEADER_ANY = ['underlying', 'contract code', 'contract month', 'quantity', 'book'];

function sheetMatchesTradeUpdateFormat(headers: string[]): boolean {
    const lower = headers.map(h => String(h || '').toLowerCase().trim());
    const hasAllRequired = TRADE_HEADER_REQUIRED.every(req => lower.some(h => h === req || h.includes(req)));
    const hasAny = TRADE_HEADER_ANY.some(a => lower.some(h => h.includes(a)));
    return hasAllRequired && hasAny;
}

/**
 * Quickly determine whether the workbook is in the multi-tab trade-update format.
 */
export function isMultiTabTradeUpdateWorkbook(workbook: XLSX.WorkBook): boolean {
    if (!workbook?.SheetNames?.length) return false;
    let matchingTabs = 0;
    for (const name of workbook.SheetNames) {
        const posType = positionTypeFromTabName(name);
        if (!posType) continue;
        const ws = workbook.Sheets[name];
        if (!ws) continue;
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1 }) as any[][];
        if (!rows.length) continue;
        const headers = (rows[0] || []).map(h => String(h ?? ''));
        if (sheetMatchesTradeUpdateFormat(headers)) matchingTabs++;
    }
    return matchingTabs >= 1;
}

// --- Per-sheet row parsing ---

function parseSheetRows(
    sheetName: string,
    rawRows: Record<string, any>[],
    positionType: PositionType,
    customerData: GetCustomerDataResponse | undefined,
    rowIdSeed: number
): MultiTabParsedRow[] {
    const out: MultiTabParsedRow[] = [];

    rawRows.forEach((row, idx) => {
        // Skip blank rows
        if (!row || Object.values(row).every(v => v === undefined || v === null || v === '')) return;

        const sideRaw = getColValue(row, ['Type', 'Side', 'Buy/Sell', 'Direction']);
        const side = looseEnumMatch(sideRaw, Side) ?? Side.Buy;

        const tradeDateRaw = getColValue(row, ['Effective Date', 'Trade Date', 'Date']);
        const tradeDate = dateToIso(tradeDateRaw) ?? dayjs().format('YYYY-MM-DD');

        const commodityRaw = getColValue(row, ['Underlying', 'Contract Code', 'Commodity', 'Product']);
        const bookRaw = getColValue(row, ['Book', 'Location', 'Plant', 'Site']);
        const counterpartyRaw = getColValue(row, ['Counterparty', 'Broker', 'Vendor', 'Supplier']);
        const portfolioRaw = getColValue(row, ['Portfolio', 'Account', 'Trader']);

        const commodityMatch = bestMatch(commodityRaw, customerData?.commodities, 'commodityName');
        const locationMatch = bestMatch(bookRaw, customerData?.locations, 'locationName');
        let counterpartyMatch = bestMatch(counterpartyRaw, customerData?.counterparties, 'counterpartyName');
        const portfolioMatch = bestMatch(portfolioRaw, customerData?.portfolios, 'portfolioName');

        // Futures and swaps from this format are exchange-traded; default the counterparty to CBOT.
        let effectiveCounterpartyName = counterpartyRaw !== undefined ? String(counterpartyRaw) : undefined;
        if (positionType === PositionType.Future || positionType === PositionType.Swap) {
            const cbotMatch = bestMatch('CBOT', customerData?.counterparties, 'counterpartyName', 0.4);
            if (cbotMatch) {
                counterpartyMatch = cbotMatch;
            }
            effectiveCounterpartyName = 'CBOT';
        }

        const volumeRaw = getColValue(row, ['Quantity', 'Volume', 'Qty']);
        const priceRaw = getColValue(row, ['Price', 'Rate', 'Trade Price']);
        // This workbook always reports quantities in contracts, so the file's "Units"
        // column is intentionally ignored and the row's unit is forced to Contract.
        const currencyRaw = getColValue(row, ['Currency', 'Ccy']);

        const unit = Unit.Contract;
        const currency = looseEnumMatch(currencyRaw, Currency) ?? Currency.Usd;

        // Budget/contract date resolution.
        //  - Swaps: "Start Date" drives both budget and contract month/year.
        //  - Futures: "Contract Month" drives both.
        // If both are present, Start Date wins for budget, Contract Month wins for contract.
        const startDateRaw = getColValue(row, ['Start Date']);
        const contractMonthRaw = getColValue(row, ['Contract Month', 'Futures Month', 'Reference Month', 'Delivery Month']);
        const contractYearRaw = getColValue(row, ['Contract Year', 'Futures Year', 'Delivery Year']);

        const startMy = dateToMonthYear(startDateRaw);
        const contractMy = dateToMonthYear(contractMonthRaw) ??
            (contractMonthRaw !== undefined && contractYearRaw !== undefined &&
                !isNaN(Number(contractMonthRaw)) && !isNaN(Number(contractYearRaw))
                ? { month: Number(contractMonthRaw), year: Number(contractYearRaw) }
                : null);

        const fallbackMy = { month: dayjs().month() + 1, year: dayjs().year() };
        const budgetDate = startMy ?? contractMy ?? fallbackMy;
        const contractDate = contractMy ?? startMy ?? fallbackMy;

        // Collect unmapped columns (anything we did not consume).
        const consumedAliases = new Set<string>();
        const trackKey = (aliases: string[]) => {
            const k = findHeaderKey(row, aliases);
            if (k) consumedAliases.add(k);
        };
        trackKey(['Type', 'Side', 'Buy/Sell', 'Direction']);
        trackKey(['Effective Date', 'Trade Date', 'Date']);
        trackKey(['Underlying', 'Contract Code', 'Commodity', 'Product']);
        trackKey(['Book', 'Location', 'Plant', 'Site']);
        trackKey(['Counterparty', 'Broker', 'Vendor', 'Supplier']);
        trackKey(['Portfolio', 'Account', 'Trader']);
        trackKey(['Quantity', 'Volume', 'Qty']);
        trackKey(['Price', 'Rate', 'Trade Price']);
        trackKey(['Units', 'Unit', 'UOM']);
        trackKey(['Currency', 'Ccy']);
        trackKey(['Start Date']);
        trackKey(['Contract Month', 'Futures Month', 'Reference Month', 'Delivery Month']);
        trackKey(['Contract Year', 'Futures Year', 'Delivery Year']);

        const unmappedData: Record<string, any> = {};
        for (const [k, v] of Object.entries(row)) {
            if (!consumedAliases.has(k) && v !== undefined && v !== null && v !== '') {
                unmappedData[k] = v;
            }
        }
        // Carry the originating tab so reviewers know which sheet a row came from.
        unmappedData['__sheet'] = sheetName;

        out.push({
            rowNumber: idx + 2,
            id: `multi_${rowIdSeed}_${idx}_${Date.now()}`,
            sheetName,
            data: {
                commodityName: commodityRaw !== undefined ? String(commodityRaw) : undefined,
                counterpartyName: effectiveCounterpartyName,
                locationName: bookRaw !== undefined ? String(bookRaw) : undefined,
                portfolioName: portfolioRaw !== undefined ? String(portfolioRaw) : undefined,
                tradeDate,
                dealTicketNumber: '',
                volume: Number(volumeRaw) || 0,
                price: Number(priceRaw) || 0,
                unit,
                currency,
                side,
                positionType,
                budgetDate: { month: budgetDate.month, year: budgetDate.year } as ContractDate,
                contractDate: { month: contractDate.month, year: contractDate.year } as ContractDate,
            },
            mappedData: {
                commodity: commodityMatch,
                counterparty: counterpartyMatch,
                location: locationMatch,
                portfolio: portfolioMatch
            },
            unmappedData,
            errors: [],
            warnings: []
        });
    });

    return out;
}

export interface MultiTabParseResult {
    rows: MultiTabParsedRow[];
    processedTabs: { name: string; positionType: PositionType; rowCount: number }[];
    skippedTabs: string[];
}

/**
 * Parses a multi-tab trade-update workbook into ParsedPositionRow-compatible rows.
 * Returns null if the workbook is not recognised as this format.
 */
export async function parseMultiTabTradeFile(
    file: File,
    customerData: GetCustomerDataResponse | undefined
): Promise<MultiTabParseResult | null> {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(new Uint8Array(buffer), { type: 'array' });

    if (!isMultiTabTradeUpdateWorkbook(workbook)) {
        return null;
    }

    const rows: MultiTabParsedRow[] = [];
    const processedTabs: { name: string; positionType: PositionType; rowCount: number }[] = [];
    const skippedTabs: string[] = [];
    let seed = 0;

    for (const sheetName of workbook.SheetNames) {
        const positionType = positionTypeFromTabName(sheetName);
        if (!positionType) {
            skippedTabs.push(sheetName);
            continue;
        }
        const ws = workbook.Sheets[sheetName];
        if (!ws) {
            skippedTabs.push(sheetName);
            continue;
        }
        const headerRows = XLSX.utils.sheet_to_json(ws, { header: 1 }) as any[][];
        const headers = (headerRows[0] || []).map(h => String(h ?? ''));
        if (!sheetMatchesTradeUpdateFormat(headers)) {
            skippedTabs.push(sheetName);
            continue;
        }
        const json = XLSX.utils.sheet_to_json(ws) as Record<string, any>[];
        const parsed = parseSheetRows(sheetName, json, positionType, customerData, seed++);
        rows.push(...parsed);
        processedTabs.push({ name: sheetName, positionType, rowCount: parsed.length });
    }

    return { rows, processedTabs, skippedTabs };
}
